import { useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Gap between the trigger and the card, and the margin the card keeps from the
// viewport's right edge. Numbers rather than classes: the card is placed by
// measurement, so they enter the same arithmetic.
const OFFSET_PX = 8;
const VIEWPORT_MARGIN_PX = 8;

/**
 * A dark explanatory card that hangs under its trigger while the pointer or
 * the keyboard focus is on it (Figma "UX/UI Desktop Version",
 * "tooltip · Evidence Mode"). Desktop only by nature: nothing hovers on a
 * phone, where the same explanation belongs in a sheet.
 *
 * Portalled and `fixed`, like `PopoverCard`: its triggers sit in scrolling,
 * clipping columns (the context panel), and the card is wider than some of
 * them. It is placed from the trigger's rect and pulled back inside the
 * viewport when the trigger is near its right edge, and it opens above the
 * trigger when the space below would cut it (a trigger at the bottom of a
 * panel).
 *
 * `side: "end"` hangs it beside the trigger instead, centred on it: for a
 * tall, thin trigger such as the sidebar's resize edge.
 *
 * `content` picks the face. A `card` explains its trigger in a title and a
 * body. A `label` names an icon-only control on one line (Figma
 * "Tooltip/Type=Label"). Its control already names itself through its
 * `aria-label`, so it leaves the description id unused; the id still resolves.
 */
export function Tooltip({
  content,
  trigger,
  side,
}: {
  content:
    | { kind: "card"; title: string; body: string }
    | { kind: "label"; text: string };
  /**
   * The trigger and how it is wrapped. `inline`: a focusable span around
   * `content`, which is not interactive itself (a link or a button would take
   * the focus twice). `box`: a span laid out by `className` around a `control`
   * that owns its focus (the tooltip opens on that focus too, which bubbles).
   * The control is handed the id of the explanation, for its own
   * `aria-describedby`: the description has to sit on the focused element.
   * Pressing in a box closes the card: the press starts what the card
   * explains.
   */
  trigger:
    | { kind: "inline"; content: React.ReactNode }
    | {
        kind: "box";
        className: string;
        control: (descriptionId: string) => React.ReactNode;
      };
  side: "below" | "end";
}): React.JSX.Element {
  const tooltipId = useId();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const trigger = triggerRef.current;
    if (trigger === null)
      throw new Error("should not happen: an open tooltip without a trigger");
    const rect = trigger.getBoundingClientRect();
    const card = cardRef.current;
    const cardRect = card === null ? null : card.getBoundingClientRect();
    const width = cardRect === null ? 0 : cardRect.width;
    const height = cardRect === null ? 0 : cardRect.height;
    const maxLeft =
      document.documentElement.clientWidth - width - VIEWPORT_MARGIN_PX;
    if (side === "end") {
      const maxTop =
        document.documentElement.clientHeight - height - VIEWPORT_MARGIN_PX;
      setPosition({
        top: Math.max(
          VIEWPORT_MARGIN_PX,
          Math.min(rect.top + (rect.height - height) / 2, maxTop)
        ),
        left: Math.min(rect.right + OFFSET_PX, maxLeft),
      });
      return;
    }
    const below = rect.bottom + OFFSET_PX;
    const fitsBelow =
      below + height + VIEWPORT_MARGIN_PX <=
      document.documentElement.clientHeight;
    setPosition({
      // Above, but never past the viewport's top edge: in a short window
      // neither side fits, and the start of the explanation matters most.
      top: fitsBelow
        ? below
        : Math.max(VIEWPORT_MARGIN_PX, rect.top - OFFSET_PX - height),
      left: Math.max(VIEWPORT_MARGIN_PX, Math.min(rect.left, maxLeft)),
    });
  }, [isOpen, side]);

  return (
    <>
      <span
        ref={triggerRef}
        tabIndex={trigger.kind === "inline" ? 0 : undefined}
        aria-describedby={
          trigger.kind === "inline" && isOpen ? tooltipId : undefined
        }
        className={
          trigger.kind === "inline"
            ? "inline-flex rounded outline-none focus-visible:shadow-focus-ring"
            : trigger.className
        }
        onPointerDown={() => {
          if (trigger.kind === "box") setIsOpen(false);
        }}
        onMouseEnter={() => {
          setIsOpen(true);
        }}
        onMouseLeave={() => {
          setIsOpen(false);
        }}
        onFocus={() => {
          setIsOpen(true);
        }}
        onBlur={() => {
          setIsOpen(false);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") setIsOpen(false);
        }}
      >
        {trigger.kind === "inline" ? (
          trigger.content
        ) : (
          <>
            {trigger.control(`${tooltipId}-description`)}
            {/* Always in the tree, so a screen reader finds the description
                the moment the control takes the focus, before the card. */}
            <span id={`${tooltipId}-description`} className="sr-only">
              {content.kind === "card"
                ? `${content.title}. ${content.body}`
                : content.text}
            </span>
          </>
        )}
      </span>
      {isOpen &&
        createPortal(
          <div
            ref={cardRef}
            id={tooltipId}
            role="tooltip"
            // Measured before it is shown: the first pass renders it hidden at
            // the origin, the layout effect places it before paint.
            className={
              content.kind === "card"
                ? "pointer-events-none fixed z-9999 flex w-70 flex-col gap-1 rounded-lg bg-ds-black p-3 font-dm-sans text-footnote text-ds-white shadow-popover"
                : "pointer-events-none fixed z-9999 rounded-lg bg-ds-black/88 px-2.5 py-1.5 font-dm-sans text-desktop-meta whitespace-nowrap text-ds-white"
            }
            style={
              position === null
                ? { top: 0, left: 0, visibility: "hidden" }
                : { top: position.top, left: position.left }
            }
          >
            {content.kind === "card" ? (
              <>
                <span className="font-semibold">{content.title}</span>
                <span className="text-ds-white/90">{content.body}</span>
              </>
            ) : (
              content.text
            )}
          </div>,
          document.body
        )}
    </>
  );
}
